import React from 'react';
import { motion } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../components/common/SafeIcon';

const Demo = () => {
  const APP_URL = "https://app.engagedent.ai";
  const VIDEO_ID = "t9C0TIDfnUE";
  
  // YouTube Parameters:
  // autoplay=1 (start immediately)
  // mute=1 (required for autoplay in most browsers)
  // loop=1 & playlist=ID (required to loop a single video)
  // cc_load_policy=1 (force captions on)
  // rel=0 (don't show related videos from other channels)
  const embedUrl = `https://www.youtube.com/embed/${VIDEO_ID}?autoplay=1&mute=1&loop=1&playlist=${VIDEO_ID}&cc_load_policy=1&rel=0`;

  return (
    <div className="bg-slate-50 min-h-screen pb-20">
      {/* Header Section */}
      <div className="bg-slate-900 text-white py-20 px-4">
        <div className="max-w-7xl mx-auto text-center">
          <motion.div 
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            className="inline-block mb-4 px-4 py-1.5 rounded-full bg-cyan-500/20 text-cyan-400 text-sm font-semibold border border-cyan-500/30"
          >
            Product Walkthrough
          </motion.div>
          <motion.h1 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="text-4xl md:text-6xl font-bold mb-6"
          >
            See EngageDent<span className="text-cyan-500">.ai</span> in Action
          </motion.h1>
          <motion.p 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="text-xl text-slate-300 max-w-2xl mx-auto"
          >
            Discover how our AI-powered platform transforms your practice's social media presence in under 5 minutes.
          </motion.p>
        </div>
      </div>

      {/* Video Section */}
      <div className="max-w-5xl mx-auto px-4 -mt-12 relative z-10">
        <motion.div 
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ delay: 0.3 }}
          className="bg-slate-900 rounded-3xl shadow-2xl overflow-hidden border-8 border-white aspect-video relative group"
        >
          <iframe 
            className="w-full h-full"
            src={embedUrl}
            title="EngageDent AI Demo Video"
            frameBorder="0"
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
            allowFullScreen
          ></iframe>
        </motion.div>

        {/* Video Highlights */}
        <div className="grid md:grid-cols-3 gap-8 mt-16">
          {[
            { 
              icon: FiIcons.FiCpu, 
              title: "AI Scripting", 
              desc: "See how our AI writes clinically accurate patient education scripts." 
            },
            { 
              icon: FiIcons.FiUserCheck, 
              title: "Avatar Selection", 
              desc: "Watch how easy it is to pick and customize your digital spokesperson." 
            },
            { 
              icon: FiIcons.FiZap, 
              title: "One-Click Export", 
              desc: "Observe the seamless transition from generation to social media posting." 
            }
          ].map((item, idx) => (
            <div key={idx} className="bg-white p-6 rounded-2xl shadow-sm border border-slate-100">
              <div className="w-12 h-12 bg-cyan-100 text-cyan-600 rounded-lg flex items-center justify-center mb-4">
                <SafeIcon icon={item.icon} className="text-xl" />
              </div>
              <h3 className="font-bold text-slate-900 mb-2">{item.title}</h3>
              <p className="text-slate-600 text-sm">{item.desc}</p>
            </div>
          ))}
        </div>

        {/* Final CTA */}
        <div className="mt-20 text-center bg-white rounded-3xl p-12 shadow-xl border border-slate-100">
          <h2 className="text-3xl font-bold text-slate-900 mb-4">Ready to try it yourself?</h2>
          <p className="text-slate-600 mb-8 max-w-xl mx-auto">
            Get started today with 3 free video credits. No credit card required.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a href={APP_URL} className="px-8 py-4 bg-cyan-500 text-white rounded-xl font-bold shadow-lg shadow-cyan-500/30 hover:bg-cyan-600 transition-all transform hover:-translate-y-1">
              Start Your Free Trial
            </a>
            <a href="/pricing" className="px-8 py-4 bg-slate-100 text-slate-900 rounded-xl font-bold hover:bg-slate-200 transition-all text-center">
              View All Plans
            </a>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Demo;